<script setup lang="ts">
import { computed, inject, type PropType } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { ObjectSubTypesSelect } from '@/entities/Inventory/ObjectSubTypes'
import {
  NULL_SELECT_TITLE,
  NULL_SELECT_VALUE,
} from '@/entities/Inventory/ObjectSubTypes/model/helpers.ts'
import { resolveListFieldDisplayValue } from '@/entities/Inventory/TypeField/model/listFieldDisplayValue.ts'
import { useMandatoryFieldError } from '../../model/useMandatoryFieldError'

const props = defineProps({
  type: {
    type: String as PropType<'list' | 'list_types'>,
    default: 'list',
  },
  field: {
    type: Object as PropType<ObjectInventoryField>,
    default: () => ({}) as ObjectInventoryField,
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  object_type: {
    type: Number,
    default: 0,
  },
  mandatory: {
    default: false,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
})

const value = defineModel()
const updateParentField = inject<(fieldId: string, value: string) => void>(
  'updateLinkedFieldValue',
  () => {},
)

/** Приводит элемент списка к форме { title, value } для VSelect. */
const toSelectItem = (item: unknown) => {
  const name =
    item && typeof item === 'object' && 'name' in item
      ? String((item as any).name ?? '')
      : String(item ?? '')
  return { title: name, value: name }
}

/**
 * Опции для VSelect типа `list`. В одиночный селект первой добавляем
 * опцию «пусто», чтобы значение всегда можно было сбросить в null.
 */
const selectItems = computed(() => {
  const real = (props.items as unknown[]).map(toSelectItem)
  if (props.multiple) return real
  return [{ title: NULL_SELECT_TITLE, value: NULL_SELECT_VALUE }, ...real]
})

const displayedValue = computed({
  get: () => {
    const resolved = resolveListFieldDisplayValue(value.value, props.items, props.multiple)
    if (props.multiple) return resolved
    return resolved == null || resolved === '' ? NULL_SELECT_VALUE : resolved
  },
  set: (newValue: unknown) => {
    if (!props.multiple && newValue === NULL_SELECT_VALUE) {
      value.value = ''
      return
    }
    value.value = newValue
  },
})

const {
  visible: mandatoryErrorVisible,
  message: mandatoryErrorMessage,
  markTouched,
  registerUserInteraction,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.mandatory,
    type: props.type,
  }),
  () => value.value,
)

const onMenuToggle = (open: boolean) => {
  if (!open) markTouched()
}

function notifyLinkedFieldValue(next: unknown) {
  if (props.type !== 'list_types') return
  const fieldId = props.field?.id
  if (fieldId == null || fieldId === '') return
  if (next === null || next === undefined || next === '') return
  updateParentField(String(fieldId), String(next))
}
</script>

<template>
  <VSelect
    v-bind="$attrs"
    v-if="type == 'list'"
    v-model="displayedValue"
    :hide-details="mandatoryErrorVisible ? false : true"
    :items="selectItems"
    :error="mandatoryErrorVisible ? true : undefined"
    :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
    item-title="title"
    item-value="value"
    :multiple="multiple"
    :label="label"
    @pointerdown="registerUserInteraction()"
    @update:menu="onMenuToggle"
  />

  <ObjectSubTypesSelect
    v-bind="$attrs"
    v-if="type == 'list_types'"
    v-model="value"
    :type-id="object_type"
    :fallback-items="items"
    :label="label"
    :error="mandatoryErrorVisible ? true : undefined"
    :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
    :hide-details="mandatoryErrorVisible ? false : true"
    @pointerdown="registerUserInteraction()"
    @update:menu="onMenuToggle"
    @update:model-value="notifyLinkedFieldValue"
  />
</template>
